import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  Truck, Car, Plus, Pencil, Trash2, Loader2, Upload, X, Image as ImageIcon,
  Ruler, Package, Fuel, Calendar, Layers, AlertCircle
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { formatCurrency } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Vehicle Card Component (defined outside main component)
function VehicleCard({ vehicle, typeInfo, onEdit, onDelete, onImageUpload }) {
  const isAuto = typeInfo.category === "auto";

  return (
    <Card>
      <div className="aspect-video bg-muted relative group">
        {vehicle.images?.[0] ? (
          <img
            src={vehicle.images[0].startsWith("http") ? vehicle.images[0] : `${process.env.REACT_APP_BACKEND_URL}${vehicle.images[0]}`}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {isAuto ? (
              <Car className="h-12 w-12 text-muted-foreground" />
            ) : (
              <Truck className="h-12 w-12 text-muted-foreground" />
            )}
          </div>
        )}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => onImageUpload(vehicle)}>
            <Upload className="h-4 w-4 mr-1" />
            Foto
          </Button>
        </div>
        <Badge className={`absolute top-3 right-3 ${vehicle.is_available !== false ? "bg-green-600" : "bg-red-600"}`}>
          {vehicle.is_available !== false ? "Disponibile" : "Non disponibile"}
        </Badge>
      </div>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-bold text-lg" style={{ fontFamily: "Chivo, sans-serif" }}>
            {vehicle.brand} {vehicle.model}
          </h3>
          {vehicle.license_plate && (
            <span className="text-xs font-mono text-muted-foreground">{vehicle.license_plate}</span>
          )}
        </div>

        <div className="mb-3">
          <Badge variant="outline" className={`${isAuto ? "border-blue-500 text-blue-600" : "border-orange-500 text-orange-600"}`}>
            {isAuto ? <Car className="h-3 w-3 mr-1" /> : <Truck className="h-3 w-3 mr-1" />}
            {typeInfo.label}
          </Badge>
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Ruler className="h-4 w-4" />
            {vehicle.length_m}m × {vehicle.height_m}m
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Package className="h-4 w-4" />
            {vehicle.cargo_volume_m3}m³
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Fuel className="h-4 w-4" />
            {vehicle.engine_type}
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            {vehicle.year}
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          <p className="text-sm text-muted-foreground">
            {vehicle.seats} posti • {vehicle.transmission}
          </p>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={() => onEdit(vehicle)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(vehicle.vehicle_id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const initialVehicle = {
  brand: "",
  model: "",
  vehicle_type: "medio",
  engine_type: "Diesel",
  length_m: "",
  height_m: "",
  width_m: "",
  cargo_volume_m3: "",
  max_payload_kg: "",
  seats: 3,
  transmission: "Manuale",
  year: new Date().getFullYear(),
  license_plate: "",
  description: "",
  features: []
};

export default function CompanyVehicles() {
  const { user, token } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState(initialVehicle);
  const [featureInput, setFeatureInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [availableVehicleTypes, setAvailableVehicleTypes] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("all");
  
  // Image upload state
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedVehicleForImage, setSelectedVehicleForImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchVehicleTypes();
    fetchVehicles();
    fetchPricing();
  }, []);

  const fetchVehicleTypes = async () => {
    try {
      const res = await axios.get(`${API}/vehicle-types`);
      setVehicleTypes(res.data);
    } catch (error) {
      console.error("Error fetching vehicle types:", error);
    }
  };

  const fetchPricing = async () => {
    if (!user?.company_id) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/companies/${user.company_id}/pricing`, { headers });
      const pricing = res.data?.pricing || [];
      const typesWithPricing = pricing
        .filter(p => p.tiers && p.tiers.length > 0 && p.tiers.some(t => t.price > 0))
        .map(p => p.vehicle_type);
      setAvailableVehicleTypes(typesWithPricing);
    } catch (error) {
      console.error("Error fetching pricing:", error);
      setAvailableVehicleTypes([]);
    }
  };

  const fetchVehicles = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/vehicles?company_id=${user?.company_id}`, { headers, withCredentials: true });
      setVehicles(res.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getVehicleTypeInfo = (typeId) => {
    return vehicleTypes.find(vt => vt.id === typeId) || { label: typeId, category: "altro" };
  };

  // Filter vehicles by category
  const filteredVehicles = vehicles.filter(v => {
    if (categoryFilter === "all") return true;
    const typeInfo = getVehicleTypeInfo(v.vehicle_type);
    return typeInfo.category === categoryFilter;
  });

  const furgoniVehicles = vehicles.filter(v => getVehicleTypeInfo(v.vehicle_type).category === "furgone");
  const autoVehicles = vehicles.filter(v => getVehicleTypeInfo(v.vehicle_type).category === "auto");

  // Get available types grouped by category
  const availableFurgoni = vehicleTypes.filter(vt => 
    vt.category === "furgone" && availableVehicleTypes.includes(vt.id)
  );
  const availableAuto = vehicleTypes.filter(vt => 
    vt.category === "auto" && availableVehicleTypes.includes(vt.id)
  );

  const openAddDialog = () => {
    setEditingVehicle(null);
    // Set default vehicle type to first available
    const defaultType = availableVehicleTypes[0] || "medio";
    setFormData({ ...initialVehicle, vehicle_type: defaultType });
    setShowDialog(true);
  };

  const openEditDialog = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      brand: vehicle.brand || "",
      model: vehicle.model || "",
      vehicle_type: vehicle.vehicle_type || "medio",
      engine_type: vehicle.engine_type || "Diesel",
      length_m: vehicle.length_m || "",
      height_m: vehicle.height_m || "",
      width_m: vehicle.width_m || "",
      cargo_volume_m3: vehicle.cargo_volume_m3 || "",
      max_payload_kg: vehicle.max_payload_kg || "",
      seats: vehicle.seats || 3,
      transmission: vehicle.transmission || "Manuale",
      year: vehicle.year || new Date().getFullYear(),
      license_plate: vehicle.license_plate || "",
      description: vehicle.description || "",
      features: vehicle.features || []
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const data = {
        ...formData,
        company_id: user.company_id,
        length_m: parseFloat(formData.length_m),
        height_m: parseFloat(formData.height_m),
        width_m: parseFloat(formData.width_m),
        cargo_volume_m3: parseFloat(formData.cargo_volume_m3),
        max_payload_kg: parseFloat(formData.max_payload_kg),
        year: parseInt(formData.year),
        seats: parseInt(formData.seats),
        license_plate: formData.license_plate || null
      };

      if (editingVehicle) {
        await axios.put(`${API}/vehicles/${editingVehicle.vehicle_id}`, data, { headers, withCredentials: true });
        toast.success("Veicolo aggiornato");
      } else {
        await axios.post(`${API}/vehicles`, data, { headers, withCredentials: true });
        toast.success("Veicolo aggiunto");
      }

      setShowDialog(false);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteVehicle = async (vehicleId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo veicolo?")) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/vehicles/${vehicleId}`, { headers, withCredentials: true });
      toast.success("Veicolo eliminato");
      fetchVehicles();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const addFeature = () => {
    if (featureInput.trim() && !formData.features.includes(featureInput.trim())) {
      setFormData({ ...formData, features: [...formData.features, featureInput.trim()] });
      setFeatureInput("");
    }
  };

  const removeFeature = (feature) => {
    setFormData({ ...formData, features: formData.features.filter(f => f !== feature) });
  };

  // Image upload handlers
  const openImageDialog = (vehicle) => {
    setSelectedVehicleForImage(vehicle);
    setShowImageDialog(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedVehicleForImage) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 5MB)");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/vehicles/${selectedVehicleForImage.vehicle_id}/images`,
        formData,
        { headers, withCredentials: true }
      );
      toast.success("Immagine caricata");
      setShowImageDialog(false);
      fetchVehicles();
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (vehicle, imageUrl) => {
    if (!window.confirm("Eliminare questa immagine?")) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/vehicles/${vehicle.vehicle_id}/images`, {
        headers,
        withCredentials: true,
        data: { image_url: imageUrl }
      });
      toast.success("Immagine eliminata");
      fetchVehicles();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
              I Miei <span className="text-primary">Veicoli</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Gestisci la tua flotta di veicoli
            </p>
          </div>
          <Button onClick={openAddDialog} className="btn-primary" disabled={availableVehicleTypes.length === 0}>
            <Plus className="mr-2 h-4 w-4" />
            Aggiungi Veicolo
          </Button>
        </div>

        {/* Stats */}
        <div className="flex gap-4 text-sm text-muted-foreground flex-wrap">
          <span>{vehicles.length} veicoli totali</span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Truck className="h-4 w-4" />
            {furgoniVehicles.length} furgoni
          </span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Car className="h-4 w-4" />
            {autoVehicles.length} auto
          </span>
        </div>

        {/* Warning if no pricing configured */}
        {availableVehicleTypes.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">Configura il tariffario</p>
              <p>
                Per aggiungere veicoli devi prima configurare i prezzi nel{" "}
                <a href="/company/tariffario" className="underline font-medium">Tariffario</a>.
                Imposta i prezzi per le tipologie di veicoli che vuoi noleggiare (furgoni e/o auto).
              </p>
            </div>
          </div>
        )}

        {/* Content with Tabs */}
        {vehicles.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Truck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Nessun veicolo</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {availableVehicleTypes.length === 0
                  ? "Configura prima il tariffario per poter aggiungere veicoli"
                  : "Aggiungi il tuo primo veicolo per iniziare"}
              </p>
              {availableVehicleTypes.length > 0 && (
                <Button onClick={openAddDialog}>Aggiungi Veicolo</Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="all" className="w-full" onValueChange={setCategoryFilter}>
            <TabsList className="mb-6">
              <TabsTrigger value="all" className="flex items-center gap-2">
                <Layers className="h-4 w-4" />
                Tutti ({vehicles.length})
              </TabsTrigger>
              <TabsTrigger value="furgone" className="flex items-center gap-2">
                <Truck className="h-4 w-4" />
                Furgoni ({furgoniVehicles.length})
              </TabsTrigger>
              <TabsTrigger value="auto" className="flex items-center gap-2">
                <Car className="h-4 w-4" />
                Auto ({autoVehicles.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vehicles.map((vehicle) => (
                  <VehicleCard 
                    key={vehicle.vehicle_id} 
                    vehicle={vehicle} 
                    typeInfo={getVehicleTypeInfo(vehicle.vehicle_type)}
                    onEdit={openEditDialog}
                    onDelete={deleteVehicle}
                    onImageUpload={openImageDialog}
                  />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="furgone">
              {furgoniVehicles.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">Nessun furgone</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {furgoniVehicles.map((vehicle) => (
                    <VehicleCard 
                      key={vehicle.vehicle_id} 
                      vehicle={vehicle} 
                      typeInfo={getVehicleTypeInfo(vehicle.vehicle_type)}
                      onEdit={openEditDialog}
                      onDelete={deleteVehicle}
                      onImageUpload={openImageDialog}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="auto">
              {autoVehicles.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">Nessuna auto</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {autoVehicles.map((vehicle) => (
                    <VehicleCard 
                      key={vehicle.vehicle_id} 
                      vehicle={vehicle} 
                      typeInfo={getVehicleTypeInfo(vehicle.vehicle_type)}
                      onEdit={openEditDialog}
                      onDelete={deleteVehicle}
                      onImageUpload={openImageDialog}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Add/Edit Vehicle Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Chivo, sans-serif" }}>
                {editingVehicle ? "Modifica Veicolo" : "Nuovo Veicolo"}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Vehicle Type */}
              <div>
                <Label className="input-label">Tipologia Veicolo *</Label>
                {availableVehicleTypes.length === 0 ? (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                    <AlertCircle className="h-4 w-4 inline mr-2" />
                    Configura prima il tariffario per abilitare le tipologie
                  </div>
                ) : (
                  <>
                    <Select
                      value={formData.vehicle_type}
                      onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                    >
                      <SelectTrigger data-testid="vehicle-type">
                        <SelectValue placeholder="Seleziona tipologia" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Furgoni Section */}
                        {availableFurgoni.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground flex items-center gap-1">
                              <Truck className="h-3 w-3" /> Furgoni
                            </div>
                            {availableFurgoni.map(vt => (
                              <SelectItem key={vt.id} value={vt.id}>
                                <span className="flex items-center gap-2">
                                  <Truck className="h-3 w-3 text-orange-500" />
                                  {vt.label}
                                </span>
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {/* Auto Section */}
                        {availableAuto.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground flex items-center gap-1 border-t mt-1 pt-2">
                              <Car className="h-3 w-3" /> Auto
                            </div>
                            {availableAuto.map(vt => (
                              <SelectItem key={vt.id} value={vt.id}>
                                <span className="flex items-center gap-2">
                                  <Car className="h-3 w-3 text-blue-500" />
                                  {vt.label}
                                </span>
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      La tipologia determina il prezzo in base al tuo tariffario
                    </p>
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="input-label">Marca *</Label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="es. Fiat, BMW, Mercedes"
                    required
                  />
                </div>
                <div>
                  <Label className="input-label">Modello *</Label>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="es. Ducato, X5, Classe A"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="input-label">Motorizzazione *</Label>
                  <Select
                    value={formData.engine_type}
                    onValueChange={(value) => setFormData({ ...formData, engine_type: value })}
                  >
                    <SelectTrigger data-testid="vehicle-engine">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Diesel">Diesel</SelectItem>
                      <SelectItem value="Benzina">Benzina</SelectItem>
                      <SelectItem value="Elettrico">Elettrico</SelectItem>
                      <SelectItem value="Ibrido">Ibrido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="input-label">Cambio *</Label>
                  <Select
                    value={formData.transmission}
                    onValueChange={(value) => setFormData({ ...formData, transmission: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Manuale">Manuale</SelectItem>
                      <SelectItem value="Automatico">Automatico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="input-label">Anno *</Label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    min="2000"
                    max={new Date().getFullYear() + 1}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="input-label">Lunghezza (m) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.length_m}
                    onChange={(e) => setFormData({ ...formData, length_m: e.target.value })}
                    placeholder="4.5"
                    required
                  />
                </div>
                <div>
                  <Label className="input-label">Altezza (m) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.height_m}
                    onChange={(e) => setFormData({ ...formData, height_m: e.target.value })}
                    placeholder="1.8"
                    required
                  />
                </div>
                <div>
                  <Label className="input-label">Larghezza (m) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.width_m}
                    onChange={(e) => setFormData({ ...formData, width_m: e.target.value })}
                    placeholder="2.0"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="input-label">Volume Carico (m³) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.cargo_volume_m3}
                    onChange={(e) => setFormData({ ...formData, cargo_volume_m3: e.target.value })}
                    placeholder="10.0"
                    required
                  />
                </div>
                <div>
                  <Label className="input-label">Portata Max (kg) *</Label>
                  <Input
                    type="number"
                    value={formData.max_payload_kg}
                    onChange={(e) => setFormData({ ...formData, max_payload_kg: e.target.value })}
                    placeholder="1000"
                    required
                  />
                </div>
                <div>
                  <Label className="input-label">Posti *</Label>
                  <Input
                    type="number"
                    value={formData.seats}
                    onChange={(e) => setFormData({ ...formData, seats: e.target.value })}
                    min="1"
                    max="9"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="input-label">Targa (Opzionale)</Label>
                <Input
                  value={formData.license_plate}
                  onChange={(e) => setFormData({ ...formData, license_plate: e.target.value.toUpperCase() })}
                  placeholder="AA000BB"
                />
              </div>

              <div>
                <Label className="input-label">Descrizione</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrizione del veicolo..."
                  rows={2}
                />
              </div>

              <div>
                <Label className="input-label">Dotazioni</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    placeholder="es. Aria condizionata, Navigatore..."
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                  />
                  <Button type="button" variant="outline" onClick={addFeature}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.features.map((feature, index) => (
                    <Badge key={index} variant="secondary" className="py-1 px-2">
                      {feature}
                      <button
                        type="button"
                        onClick={() => removeFeature(feature)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Annulla
                </Button>
                <Button type="submit" disabled={submitting} className="btn-primary">
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    editingVehicle ? "Salva Modifiche" : "Aggiungi Veicolo"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Image Upload Dialog */}
        <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Gestione Immagini</DialogTitle>
            </DialogHeader>

            {selectedVehicleForImage && (
              <div className="space-y-4">
                {/* Current images */}
                {selectedVehicleForImage.images?.length > 0 && (
                  <div>
                    <Label className="input-label mb-2">Immagini attuali</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedVehicleForImage.images.map((img, idx) => (
                        <div key={idx} className="relative group aspect-video">
                          <img
                            src={img.startsWith("http") ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`}
                            alt=""
                            className="w-full h-full object-cover rounded"
                          />
                          <button
                            type="button"
                            onClick={() => deleteImage(selectedVehicleForImage, img)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload new */}
                <div>
                  <Label className="input-label mb-2">Carica nuova immagine</Label>
                  <div className="border-2 border-dashed rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="hidden"
                      id="image-upload"
                    />
                    <label htmlFor="image-upload" className="cursor-pointer">
                      {uploading ? (
                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                      ) : (
                        <>
                          <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">
                            Clicca per caricare (max 5MB)
                          </p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowImageDialog(false)}>
                Chiudi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
